/**
 * A cancelled subscription and the exact day it ends (backend #223).
 *
 * The server sends `cancel_at` (ISO, or null) on `/auth/user` and in the
 * `plan_updated` socket payload, next to `cancel_at_period_end`. A Stripe
 * subscription can be set to end on a fixed date without `cancel_at_period_end`
 * (a cancellation scheduled from the billing portal, a custom end date), so
 * "cancelling" is either flag; the end date is `cancel_at`, falling back to the
 * plan's paid-until date for an older server that does not send it yet.
 *
 * Used by SettingsModal (Billing tab) and PlanUpgradeModal (Manage view), so
 * the two cannot disagree.
 */

const validIso = (v) => (v != null && v !== '' && Number.isFinite(Date.parse(v)) ? v : null);

/** True while the plan is set to end: `cancel_at_period_end`, or a `cancel_at` date. */
export function isPlanCancelling(user) {
  return !!user?.cancel_at_period_end || user?.cancel_at != null;
}

/** The ISO date the plan ends on: `cancel_at`, else `plan_expires_at`, else null. */
export function planEndDate(user) {
  return validIso(user?.cancel_at) || validIso(user?.plan_expires_at) || null;
}

/** "18 October 2026" / "18. Oktober 2026" in the UI language; '—' when unknown. */
export function formatPlanDate(iso, language = 'en') {
  const ms = iso ? Date.parse(iso) : NaN;
  if (!Number.isFinite(ms)) return '—';
  const locale = String(language || 'en').toLowerCase().startsWith('de') ? 'de-DE' : 'en-US';
  try {
    return new Date(ms).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return '—';
  }
}

/**
 * The user patch for a `plan_updated` payload. The payload is merged as is —
 * `cancel_at` included — except that a payload which says the subscription is
 * no longer cancelling at period end and carries no `cancel_at` at all (an
 * older server, or a resume) clears a stale `cancel_at`, so a resumed plan
 * does not keep showing "Ends on …".
 */
export function planUpdatePatch(payload) {
  if (!payload || typeof payload !== 'object') return {};
  const patch = { ...payload };
  const hasCancelAt = Object.prototype.hasOwnProperty.call(payload, 'cancel_at');
  if (hasCancelAt) patch.cancel_at = validIso(payload.cancel_at);
  else if (Object.prototype.hasOwnProperty.call(payload, 'cancel_at_period_end') && !payload.cancel_at_period_end) {
    patch.cancel_at = null;
  }
  return patch;
}
